/**
 * @file Executor base
 * @author Heino Lenting
 */
import * as tslib_1 from "tslib";
import * as moment from 'moment';
import { SubscriptionPool } from '../../core/helpers/subscription-pool';
/**
 * The executor base
 * @export
 * @class ExecutorBase
 */
var ExecutorBase = /** @class */ (function () {
    /**
     * Creates an instance of ExecutorBase.
     * @param {ExecutorName} name The name of the executor
     * @param {string} label The label of the executor
     * @param {ExecutorScope[]} scopes The supported scopes by this executor
     * @param {ExecutorAction} action The executor action
     */
    function ExecutorBase(name, label, scopes, action, category, domainService, overlayService, dropTargetsService, dialogService, translateService, userService, resourceService) {
        this.name = name;
        this.label = label;
        this.scopes = scopes;
        this.action = action;
        this.category = category;
        this.domainService = domainService;
        this.overlayService = overlayService;
        this.dropTargetsService = dropTargetsService;
        this.dialogService = dialogService;
        this.translateService = translateService;
        this.userService = userService;
        this.resourceService = resourceService;
        // The default message for the executors
        this.successMessage = 'APP.EXECUTORS.DEFAULT_SUCCESS_MESSAGE';
        /** Keep track of the subscriptions */
        this.subscriptionPool = new SubscriptionPool();
        // The default positioning value for the executors
        this.exactPositioning = false;
    }
    /**
     * The main execute method of the executor
     * @param {ExecutorData} data The executor data to make the executor stateless
     * @returns {Promise<[Entity[], string]>} The results and the (custom) success message
     */
    ExecutorBase.prototype.execute = function (data) {
        return tslib_1.__awaiter(this, void 0, void 0, function () {
            var results;
            return tslib_1.__generator(this, function (_a) {
                switch (_a.label) {
                    case 0: 
                    // First step: initiate the executor
                    return [4 /*yield*/, this.initiate()];
                    case 1:
                        // First step: initiate the executor
                        _a.sent();
                        return [4 /*yield*/, this.validate(data)];
                    case 2:
                        _a.sent();
                        return [4 /*yield*/, this.process(data)];
                    case 3:
                        results = _a.sent();
                        // Finish the result (e.q open an )
                        return [2 /*return*/, this.finish(data, results)];
                }
            });
        });
    };
    /**
     * Initiate the executor
     * @protected
     * @returns {Promise<void>}
     */
    ExecutorBase.prototype.initiate = function () {
        return tslib_1.__awaiter(this, void 0, void 0, function () {
            var hasChanges, subject, message, result;
            return tslib_1.__generator(this, function (_a) {
                switch (_a.label) {
                    case 0:
                        hasChanges = this.domainService.hasChanges();
                        if (!hasChanges) {
                            return [2 /*return*/, Promise.resolve()];
                        }
                        subject = this.translateService.instant('APP.CORE.CHANGES_TITLE');
                        message = this.translateService.instant('APP.EXECUTORS.REJECT_AND_EXECUTE_MESSAGE');
                        return [4 /*yield*/, this.dialogService.createDialog(subject, message, 'APP.CORE.OK').toPromise()];
                    case 1:
                        result = _a.sent();
                        if (!result) {
                            return [2 /*return*/, Promise.reject(new Error('Operation cancelled'))];
                        }
                        this.domainService.rejectChanges();
                        return [2 /*return*/, Promise.resolve()];
                }
            });
        });
    };
    /**
     * Validate the executor data
     * @protected
     * @param {ExecutorData} data The executor datea
     * @returns {Promise<any>}
     */
    ExecutorBase.prototype.validate = function (data) {
        // For create validation method at delete, move and drop level
        return new Promise(function (resolve, reject) {
            resolve();
        });
    };
    /**
     * The business logic for this executor and is executed after the
     * validation step and before the finish step.
     * @param {ExecutorData} data The executor data
     * @returns {Promise<any>}
     */
    ExecutorBase.prototype.process = function (data) {
        return new Promise(function (resolve, reject) {
            // data.targets.forEach(target => {
            //     data.items.forEach(item => {
            //         // YOUR MAGIC HERE
            //     })
            // });
            resolve();
        });
    };
    /**
     * Is called before the destruction of the component
     */
    ExecutorBase.prototype.ngOnDestroy = function () {
        this.subscriptionPool.unsubscribeAll();
    };
    /**
     * The default show drop targets method.
     * @param {ExecutorData} data
     */
    ExecutorBase.prototype.showDropTargets = function (data) {
    };
    /**
     * The default finish part of the executor
     * @protected
     * @param {Entity[]} result The result of the process step
     * @returns {Promise<[Entity[], string]>}
     */
    ExecutorBase.prototype.finish = function (data, result) {
        return tslib_1.__awaiter(this, void 0, void 0, function () {
            var _this = this;
            return tslib_1.__generator(this, function (_a) {
                // Here we can do things like opening the first entity etc
                return [2 /*return*/, new Promise(function (resolve, reject) {
                        resolve([result, _this.successMessage]);
                    })];
            });
        });
    };
    /**
     * Check if action is supported by this executor (used at move, delete and drop)
     * Important: executors are stateless.
     * @param {ExecutorAction} requestedAction The requested action
     * @param {ExecutorAction} supportedAction The supported action
     * @returns {Promise<any>}
     */
    ExecutorBase.prototype.isActionSupported = function (requestedAction, supportedAction) {
        return new Promise(function (resolve, reject) {
            // Test if the action is allowed
            if (requestedAction !== supportedAction) {
                return reject(new Error('This executor does not support the action "' + requestedAction + '"'));
            }
            resolve();
        });
    };
    /**
     * Validate if given Entities are resources
     * @param {*} resources The given resources
     * @returns {Promise<any>}
     */
    ExecutorBase.prototype.validateResources = function (resources) {
        return new Promise(function (resolve, reject) {
            // Test if we have any resources
            if (!resources || resources.length === 0) {
                reject(new Error('No resources are given'));
            }
            // Check if the given entities are Resources
            resources.forEach(function (resource) {
                if (!resource || !resource.entityType || resource.entityType.shortName !== 'Resource') {
                    reject(new Error('One of the resources does not look like a resource'));
                }
            });
            // Every thing is OK
            return resolve();
        });
    };
    /**
     * Validate if given Items are supported based on the scope of the executor
     * @param {Entity[]} resources The given resources
     * @returns {Promise<any>}
     */
    ExecutorBase.prototype.validateItems = function (items) {
        var _this = this;
        return new Promise(function (resolve, reject) {
            // Test if we have any items
            if (!items || items.length === 0) {
                reject(new Error('No items are given'));
            }
            // Check if the given entities are of the types given in the scope
            items.forEach(function (item) {
                if (!item || !item.entityType || _this.scopes.indexOf(item.entityType.shortName.toString()) === -1) {
                    reject(new Error('One of items can not be handled by this executor because given Type (' + item['entityType'].shortName + ') is not supported (' + _this.scopes.toString() + ').'));
                }
            });
            // Every thing is OK
            return resolve();
        });
    };
    /**
     * Validate given timeslot
     * @param {ExecutorTimeslot} timeslot
     * @returns {Promise<any>}
     */
    ExecutorBase.prototype.validateTimeslots = function (name, timeslots, isOptional) {
        var _this = this;
        if (isOptional === void 0) { isOptional = false; }
        if (!timeslots || timeslots.length === 0) {
            return new Promise(function (resolve, reject) {
                if (isOptional === true) {
                    resolve();
                }
                else {
                    reject(new Error('Expecting one or more timeslots (' + name + ')'));
                }
            });
        }
        if (!isOptional && !timeslots && timeslots.length === 0) {
            return new Promise(function (resolve, reject) {
                reject(new Error('Expecting one or more timeslots (' + name + ')'));
            });
        }
        // Run through the timeslots to validate them one by one
        var validators = [];
        timeslots.forEach(function (timeslot) {
            validators.push(_this.validateTimeslot(name, timeslot, isOptional));
        });
        return Promise.all(validators);
    };
    /**
     * Validate given timeslot
     *
     * @param {ExecutorTimeslot} timeslot
     * @returns {Promise<any>}
     */
    ExecutorBase.prototype.validateTimeslot = function (name, timeslot, isOptional) {
        if (isOptional === void 0) { isOptional = false; }
        return new Promise(function (resolve, reject) {
            // Check if timeslot is given
            if (timeslot) {
                // Test if timeslot has a start and is a Moment object
                if (!timeslot['start'] || !moment.isMoment(timeslot['start'])) {
                    reject(new Error('The start in one of the timeslots is missing or incorrect (' + name + ')'));
                }
                // Test if timeslot has a finish and is a Moment object
                if (!timeslot['finish'] || !moment.isMoment(timeslot['finish'])) {
                    reject(new Error('The finish in one of the timeslots is missing or incorrect (' + name + ')'));
                }
            }
            else {
                // If not optional return an error
                if (!isOptional) {
                    reject(new Error('The timeslot is missing (' + name + ')'));
                }
            }
            // Every thing is OK
            return resolve();
        });
    };
    /**
     * Open the first item of the items in the sidebar right
     * @param {Entity[]} items The array of items
     * @returns {Promise<any>}
     */
    ExecutorBase.prototype.openFirstItem = function (data, items) {
        return new Promise(function (resolve, reject) {
            // Check if we have atleast one created item
            if (!items || items.length === 0) {
                return reject(new Error('Can not open first item because it does nog exists'));
            }
            var showResultExecutor = data.dropComponent !== undefined ? data.dropComponent.showResultExecutor : undefined;
            if (!showResultExecutor) {
                return reject(new Error('Show result executor is missing in drop component'));
            }
            // Show result
            data.dropComponent.showResultExecutor(items);
            // Every thing is OK
            return resolve();
        });
    };
    /**
     * Generic approach for saving the changes via a Promise
     * @protected
     * @param {(reason?: any) => void} reject
     * @param {((value?: {} | PromiseLike<{}>) => void)} resolve
     */
    ExecutorBase.prototype.saveChanges = function (reject, resolve) {
        this.subscriptionPool.add(this.trySaveChanges(reject).subscribe(function (result) {
            resolve(result.entities);
        }));
    };
    /**
     * Generic approach for saving the changes and rejectchanges
     * @protected
     * @param {(reason?: any) => void} reject
     */
    ExecutorBase.prototype.trySaveChanges = function (reject) {
        var _this = this;
        return this.domainService.saveChanges(function (error) {
            if (error) {
                if (error.entityErrors && error.entityErrors.length > 0) {
                    _this.domainService.publishServerEntityErrors(error.entityErrors, null);
                }
                reject(error);
                _this.domainService.rejectChanges();
            }
        });
    };
    /**
     * Run promises parallel and then save all domain changes
     * @param tasks The array of tasks
     */
    ExecutorBase.prototype.runPromisesParallelAndSave = function (tasks) {
        var _this = this;
        return Promise.all(tasks).then(function () {
            return new Promise(function (resolve, reject) {
                _this.saveChanges(reject, resolve);
            });
        });
    };
    /**
     * Run promises serial
     * @param tasks The array of tasks
     */
    ExecutorBase.prototype.runPromisesSerial = function (slices, fn) {
        return slices.reduce(function (promise, slice) { return promise.then(function () { return fn(slice); }); }, Promise.resolve());
    };
    /**
     * Select executor data (resources, targets, and items)
     * @protected
     * @param {ExecutorData} executorData
     * @returns
     */
    ExecutorBase.prototype.explodeExecutorData = function (executorData) {
        var slices = [];
        // Resources are optional (useful for the resizes executors)
        if (!executorData.resources || executorData.resources.length === 0) {
            // Run through targets and items to create data slices
            executorData.targets.forEach(function (target) {
                executorData.items.forEach(function (item) {
                    slices.push({
                        target: target,
                        item: item
                    });
                });
            });
        }
        else {
            // Run through resources, targets, and items to create data slices
            executorData.resources.forEach(function (resource) {
                executorData.targets.forEach(function (target) {
                    executorData.items.forEach(function (item) {
                        slices.push({
                            target: target,
                            resource: resource,
                            item: item
                        });
                    });
                });
            });
        }
        return slices;
    };
    return ExecutorBase;
}());
export { ExecutorBase };
